import { z } from 'zod';
import { BooleanQueryParamSchema, createPaginatedResponseSchema, JsonObjectSchema, LowercaseUuidSchema } from './common.js';
import { ISO_WEEKDAYS, MINUTES_PER_DAY, NOTIFICATION_DELIVERY_STATUSES, NotificationCategoryKeySchema, NotificationDeliveryChannelSchema, NotificationPreferenceChannelSchema, NotificationTypeKeySchema, } from '../notification.js';
// =============================================================================
// Category and type keys in responses
// =============================================================================
/**
 * A notification category key as a RESPONSE carries it. Responses are open
 * (any string) while requests stay on the strict `NotificationCategoryKeySchema`.
 * The catalogue grows by deploy, so a browser tab still running an older bundle
 * receives keys it has never seen. A strict enum would fail the whole response
 * parse in the API client and break the bell, Settings or the monitor over one
 * unknown row. Consumers resolve the key through the catalogue and degrade
 * (skip it, use generic wording or show the raw key) when it is not there.
 */
const NotificationCategoryKeyResponseSchema = z.string();
/** A notification type key as a RESPONSE carries it; open for the same reason as the category key. */
const NotificationTypeKeyResponseSchema = z.string();
// =============================================================================
// Admin notifications monitor (super-admin, cross-account, read-only)
// =============================================================================
const ADMIN_NOTIFICATION_STATES = ['unread', 'read', 'archived'];
export const AdminNotificationStateSchema = z.enum(ADMIN_NOTIFICATION_STATES);
/**
 * Filters for the admin notification list. `from` (inclusive) and `to`
 * (exclusive) bound `createdAt` and are optional; the admin page sends the start
 * of the day after the chosen end date as `to`. `state` is the recipient's view of the row: `archived` rows are
 * those with `archivedAt` set; `unread` and `read` apply to rows that are not
 * archived. Omit `state` for every row regardless.
 */
export const AdminNotificationQuerySchema = z.strictObject({
    accountId: LowercaseUuidSchema.optional(),
    userId: LowercaseUuidSchema.optional(),
    type: NotificationTypeKeySchema.optional(),
    /** Only the rows raised for this announcement, whichever of its two notification types they are. */
    announcementId: LowercaseUuidSchema.optional(),
    state: AdminNotificationStateSchema.optional(),
    from: z.iso.datetime({ error: 'from must be a valid ISO datetime string' }).optional(),
    to: z.iso.datetime({ error: 'to must be a valid ISO datetime string' }).optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).prefault(50),
});
export const AdminNotificationRecipientSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
});
/**
 * One external-channel delivery of a notification. `skipReason` and `failureCode`
 * are the stored strings (validated against the catalogue unions on write), so a
 * reason added later reaches the monitor without a schema change. `dispatchKey`
 * and `dispatchAt` are null on a delivery recorded as skipped.
 */
export const AdminNotificationDeliverySchema = z.object({
    id: z.string(),
    channel: NotificationDeliveryChannelSchema,
    status: z.enum(NOTIFICATION_DELIVERY_STATUSES),
    skipReason: z.string().nullable(),
    failureCode: z.string().nullable(),
    dispatchKey: z.string().nullable(),
    dispatchAt: z.string().nullable(),
    providerRef: z.string().nullable(),
    sentAt: z.string().nullable(),
    deliveredAt: z.string().nullable(),
    updatedAt: z.string(),
});
export const AdminNotificationSummarySchema = z.object({
    id: z.string(),
    accountId: z.string(),
    type: NotificationTypeKeyResponseSchema,
    /** Derived from `type` through the shared catalogue; never stored. */
    category: NotificationCategoryKeyResponseSchema,
    title: z.string(),
    body: z.string(),
    href: z.string().nullable(),
    payload: JsonObjectSchema,
    createdAt: z.string(),
    readAt: z.string().nullable(),
    archivedAt: z.string().nullable(),
    recipient: AdminNotificationRecipientSchema,
    /** Empty for a notification raised before deliveries were recorded. */
    deliveries: z.array(AdminNotificationDeliverySchema),
});
export const AdminNotificationListResponseSchema = createPaginatedResponseSchema(AdminNotificationSummarySchema);
export const AdminNotificationParamsSchema = z.strictObject({ notificationId: LowercaseUuidSchema });
/**
 * A notification's email re-rendered now for the admin monitor: not the copy that
 * was sent, and with an unsubscribe link that cannot unsubscribe the recipient.
 */
export const AdminNotificationEmailPreviewSchema = z.object({
    subject: z.string(),
    html: z.string(),
    text: z.string(),
});
// =============================================================================
// Member notifications (the signed-in user's own in-app notifications)
// =============================================================================
export const NotificationAccountParamsSchema = z.strictObject({
    accountId: LowercaseUuidSchema,
});
export const NotificationParamsSchema = z.strictObject({
    accountId: LowercaseUuidSchema,
    notificationId: LowercaseUuidSchema,
});
/**
 * Filters for the signed-in user's notification list. The recipient is always
 * the caller, never a parameter. `unreadOnly` defaults to false; omit
 * `category` for every category.
 */
export const ListNotificationsQuerySchema = z.strictObject({
    unreadOnly: BooleanQueryParamSchema.optional(),
    category: NotificationCategoryKeySchema.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).prefault(25),
});
export const NotificationItemSchema = z.object({
    id: z.string(),
    type: NotificationTypeKeyResponseSchema,
    /** Derived from `type` through the shared catalogue; never stored. */
    category: NotificationCategoryKeyResponseSchema,
    title: z.string(),
    body: z.string(),
    /** An app-relative path such as `/matters/:id`, or null when there is nowhere to go. */
    href: z.string().nullable(),
    createdAt: z.string(),
    readAt: z.string().nullable(),
});
export const NotificationListResponseSchema = createPaginatedResponseSchema(NotificationItemSchema);
export const UnreadNotificationCountResponseSchema = z.object({
    count: z.number().int().nonnegative(),
});
/** Result of a single-notification state change (mark read, archive). */
export const NotificationActionResponseSchema = z.object({
    success: z.boolean(),
});
/** Result of a bulk state change: how many notifications it changed. */
export const NotificationBulkActionResponseSchema = z.object({
    count: z.number().int().nonnegative(),
});
// =============================================================================
// Notification preferences (the signed-in user's own delivery choices)
// =============================================================================
/** One offered channel for a category as it applies to the user. */
export const ResolvedNotificationChannelSchema = z.object({
    channel: NotificationPreferenceChannelSchema,
    enabled: z.boolean(),
    /** A locked channel follows the catalogue default and cannot be changed. */
    locked: z.boolean(),
    source: z.enum(['explicit', 'default', 'locked']),
});
export const NotificationCategoryPreferencesSchema = z.object({
    category: NotificationCategoryKeyResponseSchema,
    /** Offered channels only. */
    channels: z.array(ResolvedNotificationChannelSchema),
});
export const NotificationPreferencesResponseSchema = z.object({
    categories: z.array(NotificationCategoryPreferencesSchema),
});
export const SetNotificationPreferenceParamsSchema = z.strictObject({
    category: NotificationCategoryKeySchema,
    channel: NotificationPreferenceChannelSchema,
});
export const SetNotificationPreferenceBodySchema = z.strictObject({
    enabled: z.boolean(),
});
// =============================================================================
// Notification unsubscribe links (public: the signed token is the only credential)
// =============================================================================
/** The token travels in the query string: it is longer than the router allows a path segment to be. */
export const NotificationUnsubscribeQuerySchema = z.strictObject({
    token: z.string().min(1).max(512),
});
/** The one category and channel a link controls, and whether that channel is now on. */
export const NotificationUnsubscribeResponseSchema = z.object({
    category: NotificationCategoryKeyResponseSchema,
    channel: NotificationDeliveryChannelSchema,
    enabled: z.boolean(),
});
// =============================================================================
// Notification delivery hours (when the signed-in user's email and WhatsApp may go out)
// =============================================================================
const deliveryHoursShape = {
    /** ISO weekdays, Monday = 1 to Sunday = 7; at least one, no repeats. */
    days: z
        .array(z.union(ISO_WEEKDAYS.map((day) => z.literal(day))))
        .min(1, { error: 'Choose at least one day' })
        .refine((days) => new Set(days).size === days.length, { error: 'A day can only be chosen once' }),
    /** Minutes from midnight, South African time. */
    startMinute: z.number().int().min(0).max(MINUTES_PER_DAY - 1),
    /** Minutes from midnight, exclusive; 1440 means midnight at the end of the day. */
    endMinute: z.number().int().min(1).max(MINUTES_PER_DAY),
};
const startBeforeEnd = (hours) => hours.startMinute < hours.endMinute;
const startBeforeEndError = { error: 'The end time must be after the start time', path: ['endMinute'] };
export const SetNotificationDeliveryHoursBodySchema = z
    .strictObject(deliveryHoursShape)
    .refine(startBeforeEnd, startBeforeEndError);
export const NotificationDeliveryHoursResponseSchema = z
    .object({
    ...deliveryHoursShape,
    /** `default` until the user saves their own hours. */
    source: z.enum(['explicit', 'default']),
})
    .refine(startBeforeEnd, startBeforeEndError);
